import type { BigWinPort } from "./BigWinPort";
import type { PresentBigWinInput } from "@slot/client-engine";
import type { BigWinTierBreakpoint } from "../../boot/settings/GameInitialSettingsTypes";
import type { BigWinTuning } from "./BigWinTuning";
import type { MoneyContext } from "../../money/MoneyContext";
import type { HapticsService } from "../../haptics/HapticsService";
import type { InteractionBlockStore } from "../../interaction-block/InteractionBlockStore";
import type { CanvasOverlayStore } from "../../canvas-overlay/CanvasOverlayStore";
import type { AudioService } from "../../audio/ports/AudioService";
import { OverlayPressBinding } from "../overlayInput";
import type { Log, PresentationTaskRunner } from "@slot/presentation";

type Phase = "counting" | "readyToClose";

// Multiplier comparison is exact-integer: cur >= round(mult·D)·bet / D. D=1000 → 3-decimal ratios.
const MULTIPLIER_DENOM = 1000n;

/** The count-up and the close wait run in their own runner scopes, so a press cuts one without touching the other. */
const COUNT_SCOPE = "big_win_count";
const CLOSE_SCOPE = "big_win_close";

interface RevealStep {
  tierId: string;
  thresholdMinor: bigint;
}

/**
 * Big-win overlay orchestration. The overlay ALWAYS opens minimal — dim + the number scrolling
 * from 0, no title. As the running amount climbs, it reveals each tier's title IN ORDER
 * (`initialSettings.bigWinTiers`, ascending) the moment the amount crosses that tier's
 * `minWinMultiplier × bet`. The FIRST titled reveal plays the full slide/fade + fires `bigwin.start`;
 * later tiers just swap the title node + scale-bump. The final tier is the server's `levelIdentifier`
 * (authoritative) and is force-revealed by count-end. (Any decoration a game brings in on the first
 * titled reveal — e.g. Game A's angels — is the panel implementation's own concern, off its own
 * first `revealTier` → `true`; the presenter neither knows nor drives it.)
 *
 * "nice" (titleless) is emergent, NOT hardcoded: a tier whose panel has no title art makes
 * `revealTier` return false. If NO title is ever shown, the win is treated as minimal — no settle
 * bump, no "tap to continue", short auto-close (`niceAutoCloseMs`), no `bigwin.start`.
 */
export class BigWinPresenter {
  private readonly panel: BigWinPort;
  private readonly log: Log;
  private readonly audioService: AudioService;
  private readonly money: MoneyContext;
  private readonly hapticsService: HapticsService;
  private readonly interactionBlockStore: InteractionBlockStore;
  private readonly canvasOverlayStore: CanvasOverlayStore;
  private readonly bigWinTiers: readonly BigWinTierBreakpoint[];
  private readonly tuning: BigWinTuning;
  private readonly runner: PresentationTaskRunner;

  private phase: Phase = "counting";
  private skipRequested = false;
  private closeRequested = false;
  private resolveClose: (() => void) | null = null;

  // Per-presentation reveal state.
  private schedule: RevealStep[] = [];
  private nextRevealIdx = 0;
  private anyTitleShown = false;

  private readonly pressBinding = new OverlayPressBinding();
  private unbindSkipTap: (() => void) | null = null;

  public constructor(input: {
    panel: BigWinPort;
    audioService: AudioService;
    hapticsService: HapticsService;
    interactionBlockStore: InteractionBlockStore;
    /** The overlay is Pixi: the DOM chrome hides while it is up. */
    canvasOverlayStore: CanvasOverlayStore;
    money: MoneyContext;
    log: Log;
    /** All client presentation knobs (auto-close + count-up shaping) — the single tuning object. */
    tuning: BigWinTuning;
    /** Big-win tier breakpoints (server-sent, from initialSettings) — drives the escalating reveal. */
    bigWinTiers?: readonly BigWinTierBreakpoint[];
    /** The chrome runner: the overlay's clock — speed-invariant, frozen by the dev pause, stepped by frame-step. */
    runner: PresentationTaskRunner;
  }) {
    this.panel = input.panel;
    this.log = input.log;
    this.money = input.money;
    this.audioService = input.audioService;
    this.hapticsService = input.hapticsService;
    this.interactionBlockStore = input.interactionBlockStore;
    this.canvasOverlayStore = input.canvasOverlayStore;
    this.tuning = input.tuning;
    this.bigWinTiers = input.bigWinTiers ?? [];
    this.runner = input.runner;
  }

  public async presentBigWin(inputData: PresentBigWinInput): Promise<void> {
    this.phase = "counting";
    this.skipRequested = false;
    this.closeRequested = false;
    this.resolveClose = null;
    this.nextRevealIdx = 0;
    this.anyTitleShown = false;
    this.schedule = this.buildSchedule(inputData);

    // Minimal opening state: titles hidden, number at 0.
    this.panel.hideTitles();
    this.panel.setSkipVisible(true);
    const initialFmt = this.panel.amountOmitsCurrencySymbol ? { omitSymbol: true } : undefined;
    this.panel.setAmount(this.money.formatter.formatMinor("0", initialFmt));

    const releaseOverlay = this.canvasOverlayStore.open();
    this.panel.showImmediate();
    this.interactionBlockStore.add("big_win");
    this.log(`[BigWin] show finalTier=${String(inputData.levelIdentifier)} amount=${inputData.spinWinAmount}`);

    this.bindGlobalInputSurface();

    try {
      // INSTANT mode: show the final tier's title at open (no progressive escalation).
      if (this.tuning.instantTierReveal) this.revealFinalInstant();

      // Number-only enter, in parallel with the count-up so the scroll starts immediately.
      void this.panel.playEnterAnimation();

      this.audioService.presentation.emit({ id: "bigwin.count_start" });
      this.panel.startAmountPulse();

      await this.animateAmountScrollSkippable(inputData.spinWinAmount);

      // Guarantee every scheduled tier up to the server's final tier is revealed (a tier whose
      // threshold the rounded count never quite reached, or all of them on skip). Instant on skip.
      this.revealPending(this.skipRequested);

      this.audioService.presentation.emit({ id: "bigwin.count_end" });

      if (this.anyTitleShown) {
        // Titled win — full lifecycle: settle bump → "tap to continue" → close.
        await this.panel.playAmountSettleBump();
        void this.panel.fadeInContinueText();
        this.phase = "readyToClose";
        await this.waitForClose(this.tuning.autoCloseTimeoutMs);
      } else {
        // Titleless ("nice") win — minimal: no settle bump, no continue hint, short auto-close.
        this.phase = "readyToClose";
        await this.waitForClose(this.tuning.niceAutoCloseMs);
      }

      this.audioService.presentation.emit({ id: "bigwin.end" });

      await this.panel.playExitAnimation();
      this.panel.hideImmediate();
      this.log("[BigWin] hide");
    } finally {
      this.unbindGlobalInputSurface();
      this.interactionBlockStore.remove("big_win");
      releaseOverlay();
      this.runner.clearScope(COUNT_SCOPE);
      this.runner.clearScope(CLOSE_SCOPE);
    }
  }

  /**
   * The reveal schedule: every tier from the lowest up to AND including the server's final tier
   * (`levelIdentifier`), ordered ascending, with each tier's threshold in minor units. The final
   * tier is authoritative — tiers above it are never revealed even if the amount would cross them.
   * Unknown final tier / empty breakpoints → no schedule (the win plays as minimal/titleless).
   */
  private buildSchedule(inputData: PresentBigWinInput): RevealStep[] {
    const tiers = [...this.bigWinTiers].sort((a, b) => a.minWinMultiplier - b.minWinMultiplier);
    if (tiers.length === 0) return [];

    const finalTier = (inputData.levelIdentifier ?? "").toLowerCase();
    const finalIdx = tiers.findIndex((t) => t.levelIdentifier.toLowerCase() === finalTier);
    if (finalIdx < 0) return []; // final tier not in the breakpoints → minimal fallback

    const betMinor = BigInt(inputData.betAmount ?? "0");

    return tiers.slice(0, finalIdx + 1).map((t) => ({
      tierId: t.levelIdentifier,
      thresholdMinor:
        betMinor > 0n ? (BigInt(Math.round(t.minWinMultiplier * Number(MULTIPLIER_DENOM))) * betMinor) / MULTIPLIER_DENOM : 0n,
    }));
  }

  /** Reveal every tier the running amount has crossed (called per count-up frame). */
  private revealCrossed(cur: bigint): void {
    while (this.nextRevealIdx < this.schedule.length && cur >= this.schedule[this.nextRevealIdx].thresholdMinor) {
      this.revealOne(this.nextRevealIdx++, false);
    }
  }

  /** Reveal all remaining scheduled tiers (count-end / skip). `instant` skips the first-reveal slide. */
  private revealPending(instant: boolean): void {
    while (this.nextRevealIdx < this.schedule.length) {
      this.revealOne(this.nextRevealIdx++, instant);
    }
  }

  /**
   * INSTANT reveal mode: show ONLY the final (highest scheduled) tier immediately, with no slide,
   * then suppress the progressive per-frame / count-end reveals (nextRevealIdx → end). If the final
   * tier is the nice tier and `niceTierNumberOnly`, `revealOne` keeps it titleless (minimal win).
   */
  private revealFinalInstant(): void {
    if (this.schedule.length === 0) return;
    this.revealOne(this.schedule.length - 1, true);
    this.nextRevealIdx = this.schedule.length;
  }

  private revealOne(i: number, instant: boolean): void {
    const step = this.schedule[i];
    // The lowest tier (index 0) is the "nice" tier. When niceTierNumberOnly, it never shows a
    // title — just the dimmed number — regardless of panel art.
    if (i === 0 && this.tuning.niceTierNumberOnly) return;
    // First TITLED reveal gets the full slide/fade unless we're forcing instant (skip / instant mode).
    const animateEnter = !this.anyTitleShown && !instant;
    const titled = this.panel.revealTier(step.tierId, animateEnter);
    if (titled && !this.anyTitleShown) {
      // The panel brings in its own decoration (Game A: angels) off this first `true` — not here.
      this.anyTitleShown = true;
      this.audioService.presentation.emit({ id: "bigwin.start" });
      this.hapticsService.play("success");
    }
  }

  private bindGlobalInputSurface(): void {
    this.unbindGlobalInputSurface();

    const onPress = (): void => {
      if (this.phase === "counting") {
        this.skipRequested = true;
        this.log("[BigWin] press => skip to final");
        this.runner.slamFinishScope(COUNT_SCOPE);
        return;
      }

      if (this.phase === "readyToClose") {
        this.closeRequested = true;
        this.log("[BigWin] press => close");
        this.resolveClose?.();
      }
    };

    this.unbindSkipTap = this.panel.bindSkipTap(onPress);
    this.pressBinding.bindKeys(onPress);
  }

  private unbindGlobalInputSurface(): void {
    this.unbindSkipTap?.();
    this.unbindSkipTap = null;
    this.pressBinding.unbindAll();
  }

  private async animateAmountScrollSkippable(targetMinor: string): Promise<void> {
    const end = BigInt(targetMinor);

    const start = 0n;
    const fmt = this.panel.amountOmitsCurrencySymbol ? { omitSymbol: true } : undefined;

    this.panel.setAmount(this.money.formatter.formatMinor("0", fmt));

    if (end <= 0n) {
      this.panel.setAmount(this.money.formatter.formatMinor("0", fmt));
      return;
    }

    const digits = end.toString().length;
    // Count-up duration scales with digit count, capped + floored — all from tuning.
    const cu = this.tuning.countUp;
    const durationSeconds = Math.min(cu.maxSeconds, cu.baseSeconds + digits * cu.perDigitSeconds);
    const durationMs = Math.max(cu.minMs, Math.floor(durationSeconds * 1000));

    const denom = 1000n;

    // One engine-time tween; a press slams its scope, which delivers t = 1 and lands the amount below.
    await this.runner.tweenProgress(
      durationMs / 1000,
      (t) => {
        if (this.skipRequested) return;
        const eased = this.applyCountUpEasing(t);
        const tn = BigInt(Math.round(eased * 1000));
        const cur = start + ((end - start) * tn) / denom;
        this.panel.setAmount(this.money.formatter.formatMinor(cur.toString(), fmt));
        this.revealCrossed(cur);
      },
      COUNT_SCOPE,
      { easing: "linear" },
    );

    this.panel.setAmount(this.money.formatter.formatMinor(end.toString(), fmt));
    // On skip the caller reveals what is left instantly; a natural end reveals the last crossing here.
    if (!this.skipRequested) this.revealCrossed(end);
  }

  /**
   * Map linear progress `t` (0→1) to the count-up speed curve from tuning.
   *   easeIn  — start slow, accelerate (number rushes at the end)
   *   easeOut — start fast, decelerate to a slow finish
   *   linear  — constant
   * `easingExponent` is the power (clamped ≥ 1); easeOut + exponent 3 reproduces the prior curve.
   */
  private applyCountUpEasing(t: number): number {
    const { easing, easingExponent } = this.tuning.countUp;
    const p = Math.max(1, easingExponent);
    if (easing === "linear") return t;
    if (easing === "easeIn") return Math.pow(t, p);
    return 1 - Math.pow(1 - t, p); // easeOut
  }

  /** Resolved by the press or by the auto-close sleep on engine time; a press cuts the sleep so nothing outlives the overlay. */
  private async waitForClose(autoCloseMs: number): Promise<void> {
    if (this.closeRequested) return;
    const pressed = new Promise<void>((resolve) => {
      this.resolveClose = resolve;
    });
    if (autoCloseMs <= 0) {
      await pressed;
      return;
    }
    const timedOut = await Promise.race([pressed.then(() => false), this.runner.sleep(autoCloseMs / 1000, CLOSE_SCOPE).then(() => true)]);
    if (timedOut) this.log(`[BigWin] auto-close timeout fired (${autoCloseMs}ms)`);
    else this.runner.slamFinishScope(CLOSE_SCOPE);
  }
}
